import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import AdminLayout from '../../components/feature/AdminLayout';
import {
  fetchAdminProducts,
  fetchAdminRelease,
  createAdminRelease,
  updateAdminRelease,
  uploadReleaseArtifact,
  deleteReleaseArtifact,
  type PlatformProduct,
  type PlatformRelease,
  type ReleaseChangeItem,
  type ReleaseArtifact,
  type ReleaseType,
  type ReleaseStatus,
  type ChangeCategory,
} from '../../api/releases.api';

const CATEGORY_META: Record<ChangeCategory, { label: string; icon: string; color: string }> = {
  feature: { label: 'ميزة جديدة (Feature)', icon: 'ri-sparkling-fill', color: 'text-emerald-600 bg-emerald-50 border-emerald-200' },
  improvement: { label: 'تحسين (Improvement)', icon: 'ri-flashlight-line', color: 'text-indigo-600 bg-indigo-50 border-indigo-200' },
  fix: { label: 'إصلاح خطأ (Bug Fix)', icon: 'ri-bug-line', color: 'text-amber-600 bg-amber-50 border-amber-200' },
  breaking: { label: 'تغيير مهم (Breaking Change)', icon: 'ri-alert-line', color: 'text-rose-600 bg-rose-50 border-rose-200' },
  security: { label: 'تحديث أمني (Security)', icon: 'ri-shield-keyhole-line', color: 'text-cyan-600 bg-cyan-50 border-cyan-200' },
};

export default function ReleaseFormPage() {
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  const navigate = useNavigate();

  const [products, setProducts] = useState<PlatformProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  // Form State
  const [productId, setProductId] = useState<number | ''>('');
  const [version, setVersion] = useState('');
  const [versionCode, setVersionCode] = useState<number | ''>('');
  const [title, setTitle] = useState('');
  const [summary, setSummary] = useState('');
  const [releaseType, setReleaseType] = useState<ReleaseType>('minor');
  const [status, setStatus] = useState<ReleaseStatus>('draft');
  const [isCurrent, setIsCurrent] = useState(false);
  const [releaseDate, setReleaseDate] = useState(new Date().toISOString().split('T')[0]);
  const [scheduledAt, setScheduledAt] = useState('');
  const [systemRequirements, setSystemRequirements] = useState('');
  const [compatibilityNotes, setCompatibilityNotes] = useState('');
  const [upgradeInstructions, setUpgradeInstructions] = useState('');

  // Changes builder
  const [changes, setChanges] = useState<ReleaseChangeItem[]>([]);

  // Artifacts list (in edit mode)
  const [artifacts, setArtifacts] = useState<ReleaseArtifact[]>([]);
  const [uploadingArtifact, setUploadingArtifact] = useState(false);
  const [artifactOs, setArtifactOs] = useState('windows');
  const [artifactArch, setArtifactArch] = useState('x64');
  const [artifactMinOs, setArtifactMinOs] = useState('Windows 10 Build 19041+');

  const loadInitialData = useCallback(async () => {
    setLoading(true);
    setError('');

    const prodRes = await fetchAdminProducts();
    if (!prodRes.ok) {
      if (prodRes.unauthorized) {
        navigate('/admin/login', { replace: true });
        return;
      }
      setError(prodRes.message);
      setLoading(false);
      return;
    }

    setProducts(prodRes.products);

    if (isEdit && id) {
      const relRes = await fetchAdminRelease(Number(id));
      if (!relRes.ok) {
        setError(relRes.message);
        setLoading(false);
        return;
      }

      const rel = relRes.release;
      setProductId(rel.product_id);
      setVersion(rel.version);
      setVersionCode(rel.version_code ?? '');
      setTitle(rel.title);
      setSummary(rel.summary ?? '');
      setReleaseType(rel.release_type);
      setStatus(rel.status);
      setIsCurrent(Boolean(rel.is_current));
      setReleaseDate(rel.release_date || new Date().toISOString().split('T')[0]);
      setScheduledAt(rel.scheduled_at ? rel.scheduled_at.replace(' ', 'T').slice(0, 16) : '');
      setSystemRequirements(rel.system_requirements ?? '');
      setCompatibilityNotes(rel.compatibility_notes ?? '');
      setUpgradeInstructions(rel.upgrade_instructions ?? '');
      setChanges(rel.changes ?? []);
      setArtifacts(rel.artifacts ?? []);
    } else {
      // Default to first product if available
      if (prodRes.products.length > 0) {
        setProductId(prodRes.products[0].id);
      }
    }

    setLoading(false);
  }, [id, isEdit, navigate]);

  useEffect(() => {
    void loadInitialData();
  }, [loadInitialData]);

  // Changes Helper
  const addChangeItem = (category: ChangeCategory = 'feature') => {
    setChanges([
      ...changes,
      {
        category,
        title: '',
        description: '',
        badge: '',
        sort_order: changes.length + 1,
      },
    ]);
  };

  const removeChangeItem = (index: number) => {
    setChanges(changes.filter((_, i) => i !== index));
  };

  const updateChangeItem = (index: number, field: keyof ReleaseChangeItem, value: unknown) => {
    setChanges(
      changes.map((item, i) => {
        if (i === index) {
          return { ...item, [field]: value };
        }
        return item;
      })
    );
  };

  // Artifact Upload
  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !id) return;

    setUploadingArtifact(true);
    setError('');

    const formData = new FormData();
    formData.append('file', file);
    formData.append('os_type', artifactOs);
    formData.append('architecture', artifactArch);
    if (artifactMinOs) {
      formData.append('min_os_version', artifactMinOs);
    }

    const res = await uploadReleaseArtifact(Number(id), formData);
    setUploadingArtifact(false);
    e.target.value = '';

    if (!res.ok) {
      setError(res.message);
      return;
    }

    setArtifacts([...artifacts, res.artifact]);
    setSuccessMsg('تم رفع ملف التثبيت وحساب بصمة SHA-256 بنجاح.');
  };

  const handleDeleteArtifact = async (artifactId: number) => {
    if (!id) return;
    if (!window.confirm('هل أنت متأكد من حذف هذا الملف؟')) return;

    const res = await deleteReleaseArtifact(Number(id), artifactId);
    if (!res.ok) {
      setError(res.message);
      return;
    }

    setArtifacts(artifacts.filter((a) => a.id !== artifactId));
    setSuccessMsg('تم حذف الملف.');
  };

  // Submit
  const handleSubmit = async (submitStatus?: ReleaseStatus) => {
    setError('');
    setSuccessMsg('');

    if (!productId) {
      setError('يرجى اختيار المنتج.');
      return;
    }
    if (!version.trim()) {
      setError('يرجى إدخال رقم الإصدار (مثل 2.4.0).');
      return;
    }
    if (!title.trim()) {
      setError('يرجى إدخال عنوان التحديث.');
      return;
    }

    const finalStatus = submitStatus || status;

    const payload = {
      product_id: Number(productId),
      version: version.trim(),
      version_code: versionCode ? Number(versionCode) : null,
      title: title.trim(),
      summary: summary.trim() || null,
      release_type: releaseType,
      status: finalStatus,
      is_current: isCurrent,
      release_date: releaseDate,
      scheduled_at: finalStatus === 'scheduled' && scheduledAt ? scheduledAt : null,
      system_requirements: systemRequirements.trim() || null,
      compatibility_notes: compatibilityNotes.trim() || null,
      upgrade_instructions: upgradeInstructions.trim() || null,
      changes: changes.filter((c) => c.title.trim().length > 0),
    };

    setSaving(true);

    if (isEdit && id) {
      const res = await updateAdminRelease(Number(id), payload);
      setSaving(false);
      if (!res.ok) {
        setError(res.message);
        return;
      }
      setSuccessMsg('تم حفظ التعديلات بنجاح.');
      // Refresh
      void loadInitialData();
    } else {
      const res = await createAdminRelease(payload);
      setSaving(false);
      if (!res.ok) {
        setError(res.message);
        return;
      }
      navigate(`/releases/${res.release.id}/edit`, { replace: true });
    }
  };

  const selectedProductObj = products.find((p) => p.id === Number(productId));
  const isDesktopProduct = selectedProductObj?.type === 'desktop';

  return (
    <AdminLayout>
      <div className="space-y-6 max-w-5xl mx-auto pb-12" dir="rtl">
        {/* Breadcrumb & Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
          <div>
            <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
              <Link to="/releases" className="hover:text-blue-600 transition-colors">
                إدارة الإصدارات
              </Link>
              <i className="ri-arrow-left-s-line" />
              <span>{isEdit ? `تعديل الإصدار v${version}` : 'إضافة إصدار جديد'}</span>
            </div>
            <h1 className="text-xl font-bold text-gray-900">
              {isEdit ? `تعديل بيانات الإصدار v${version}` : 'إضافة إصدار جديد للمنتج'}
            </h1>
          </div>

          <div className="flex items-center gap-3">
            <Link
              to="/releases"
              className="px-4 py-2 rounded-xl border border-gray-200 text-gray-700 hover:bg-gray-50 text-sm font-medium"
            >
              إلغاء
            </Link>

            {isEdit && id && (
              <Link
                to={`/releases/${id}/preview`}
                className="px-4 py-2 rounded-xl border border-blue-200 text-blue-700 bg-blue-50 hover:bg-blue-100 text-sm font-semibold flex items-center gap-1.5"
              >
                <i className="ri-eye-line" />
                معاينة
              </Link>
            )}

            <button
              onClick={() => void handleSubmit('draft')}
              disabled={saving}
              className="px-4 py-2 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-800 text-sm font-semibold disabled:opacity-50"
            >
              حفظ كمسودة
            </button>

            <button
              onClick={() => void handleSubmit('published')}
              disabled={saving}
              className="px-5 py-2 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-sm font-bold shadow-sm shadow-blue-200 disabled:opacity-50 flex items-center gap-1.5"
            >
              {saving ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-checkbox-circle-line" />}
              {status === 'published' ? 'حفظ التحديثات' : 'حفظ ونشر الآن'}
            </button>
          </div>
        </div>

        {/* Notifications */}
        {error && (
          <div className="p-4 bg-rose-50 border border-rose-200 text-rose-700 rounded-xl flex items-center gap-3">
            <i className="ri-error-warning-line text-xl" />
            <span>{error}</span>
          </div>
        )}

        {successMsg && (
          <div className="p-4 bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-xl flex items-center gap-3">
            <i className="ri-checkbox-circle-line text-xl" />
            <span>{successMsg}</span>
          </div>
        )}

        {loading ? (
          <div className="bg-white p-12 rounded-2xl border border-gray-100 text-center text-gray-500">
            <i className="ri-loader-4-line text-3xl animate-spin text-blue-600 mb-2 inline-block" />
            <p>جاري تحميل البيانات...</p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Card 1: Product & Basic Info */}
            <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm space-y-5">
              <div className="flex items-center gap-2 border-b border-gray-100 pb-3 text-base font-bold text-gray-900">
                <i className="ri-information-line text-blue-600 text-lg" />
                بيانات الإصدار الأساسية
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Product */}
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">المنتج المرتبط *</label>
                  <select
                    value={productId}
                    onChange={(e) => setProductId(Number(e.target.value))}
                    className="w-full px-3 py-2 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-blue-500 bg-white"
                  >
                    {products.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name} ({p.type})
                      </option>
                    ))}
                  </select>
                </div>

                {/* Version */}
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">رقم الإصدار (SemVer) *</label>
                  <input
                    type="text"
                    placeholder="مثال: 2.4.0"
                    value={version}
                    onChange={(e) => setVersion(e.target.value)}
                    className="w-full px-3 py-2 rounded-xl border border-gray-200 text-sm font-mono focus:outline-none focus:border-blue-500"
                  />
                  <span className="text-[11px] text-gray-400 mt-0.5 block">وفق صيغة Major.Minor.Patch</span>
                </div>

                {/* Release Type */}
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">نوع الإصدار *</label>
                  <select
                    value={releaseType}
                    onChange={(e) => setReleaseType(e.target.value as ReleaseType)}
                    className="w-full px-3 py-2 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-blue-500 bg-white"
                  >
                    <option value="major">رئيسي (Major - مميزات وبنية جديدة)</option>
                    <option value="minor">فرعي (Minor - إضافات وتحسينات)</option>
                    <option value="patch">تصحيح (Patch - إصلاحات أخطاء)</option>
                    <option value="security">أمني (Security - سد ثغرات وحماية)</option>
                  </select>
                </div>
              </div>

              {/* Title & Summary */}
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">عنوان التحديث *</label>
                  <input
                    type="text"
                    placeholder="مثال: تحديث الأداء الشامل ونظام الباركود الذكي"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    className="w-full px-3 py-2 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">الوصف المختصر للتحديث</label>
                  <textarea
                    rows={2}
                    placeholder="نبذة سريعة تلخص أبرز ما جاء في هذا الإصدار..."
                    value={summary}
                    onChange={(e) => setSummary(e.target.value)}
                    className="w-full px-3 py-2 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-blue-500"
                  />
                </div>
              </div>

              {/* Status, Date, Current Toggle */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-2 border-t border-gray-50">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">تاريخ الإصدار</label>
                  <input
                    type="date"
                    value={releaseDate}
                    onChange={(e) => setReleaseDate(e.target.value)}
                    className="w-full px-3 py-2 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">حالة النشر</label>
                  <select
                    value={status}
                    onChange={(e) => setStatus(e.target.value as ReleaseStatus)}
                    className="w-full px-3 py-2 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-blue-500 bg-white"
                  >
                    <option value="draft">مسودة (غير معروض للزوار)</option>
                    <option value="scheduled">مجدول (ينشر تلقائياً في موعد)</option>
                    <option value="published">منشور (متاح فوراً على الموقع)</option>
                    <option value="archived">مؤرشف (قديم بدون تنزيل نشط)</option>
                  </select>
                </div>

                {status === 'scheduled' && (
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">موعد النشر المجدول</label>
                    <input
                      type="datetime-local"
                      value={scheduledAt}
                      onChange={(e) => setScheduledAt(e.target.value)}
                      className="w-full px-3 py-2 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-blue-500"
                    />
                  </div>
                )}
              </div>

              {/* Is Current Checkbox */}
              <div className="pt-2">
                <label className="flex items-center gap-3 p-3 rounded-xl bg-blue-50/60 border border-blue-100 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={isCurrent}
                    onChange={(e) => setIsCurrent(e.target.checked)}
                    className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                  />
                  <div>
                    <span className="text-sm font-bold text-gray-900 block">
                      اعتماد هذا الإصدار كإصدار رئيسي حالي للمنتج (Current Release)
                    </span>
                    <span className="text-xs text-gray-500">
                      سيظهر هذا الإصدار في واجهة صفحة المنتج الرئيسية وبطاقة التنزيل الأحدث تلقائياً.
                    </span>
                  </div>
                </label>
              </div>
            </div>

            {/* Card 2: Categorized Changelog Builder */}
            <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm space-y-4">
              <div className="flex items-center justify-between border-b border-gray-100 pb-3">
                <div className="flex items-center gap-2 text-base font-bold text-gray-900">
                  <i className="ri-list-check-3 text-emerald-600 text-lg" />
                  سجل التغييرات والتحديثات التفصيلي ({changes.length} عناصر)
                </div>

                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => addChangeItem('feature')}
                    className="px-3 py-1.5 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 rounded-lg text-xs font-semibold flex items-center gap-1 transition-colors"
                  >
                    <i className="ri-add-line" />
                    ميزة جديدة
                  </button>
                  <button
                    type="button"
                    onClick={() => addChangeItem('improvement')}
                    className="px-3 py-1.5 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 rounded-lg text-xs font-semibold flex items-center gap-1 transition-colors"
                  >
                    <i className="ri-add-line" />
                    تحسين
                  </button>
                  <button
                    type="button"
                    onClick={() => addChangeItem('fix')}
                    className="px-3 py-1.5 bg-amber-50 text-amber-700 hover:bg-amber-100 rounded-lg text-xs font-semibold flex items-center gap-1 transition-colors"
                  >
                    <i className="ri-add-line" />
                    إصلاح
                  </button>
                  <button
                    type="button"
                    onClick={() => addChangeItem('security')}
                    className="px-3 py-1.5 bg-cyan-50 text-cyan-700 hover:bg-cyan-100 rounded-lg text-xs font-semibold flex items-center gap-1 transition-colors"
                  >
                    <i className="ri-add-line" />
                    أمني
                  </button>
                </div>
              </div>

              {changes.length === 0 ? (
                <div className="p-8 text-center text-gray-400 border border-dashed border-gray-200 rounded-xl">
                  <p className="text-sm">لم تتم إضافة أي بنود في سجل التغييرات بعد.</p>
                  <p className="text-xs text-gray-400 mt-1">انقر على أحد الأزرار بالأعلى لإضافة بند ميزة، تحسين، إصلاح، أو تحديث أمني.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {changes.map((item, index) => {
                    const catMeta = CATEGORY_META[item.category] || CATEGORY_META.feature;

                    return (
                      <div
                        key={index}
                        className="p-4 rounded-xl border border-gray-200 bg-gray-50/40 space-y-3 transition-all hover:border-gray-300"
                      >
                        <div className="flex items-center justify-between gap-3">
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-400 font-mono">#{index + 1}</span>
                            <select
                              value={item.category}
                              onChange={(e) => updateChangeItem(index, 'category', e.target.value)}
                              className={`px-2.5 py-1 rounded-lg border text-xs font-semibold bg-white ${catMeta.color}`}
                            >
                              <option value="feature">🚀 ميزة جديدة (Feature)</option>
                              <option value="improvement">⚡ تحسين (Improvement)</option>
                              <option value="fix">🐛 إصلاح خطأ (Fix)</option>
                              <option value="breaking">⚠️ تغيير مهم (Breaking)</option>
                              <option value="security">🔒 تحديث أمني (Security)</option>
                            </select>
                          </div>

                          <div className="flex items-center gap-2">
                            <input
                              type="text"
                              placeholder="شارة إضافية (اختياري مثل: جديد)"
                              value={item.badge ?? ''}
                              onChange={(e) => updateChangeItem(index, 'badge', e.target.value)}
                              className="px-2 py-1 rounded-lg border border-gray-200 text-xs bg-white focus:outline-none focus:border-blue-500 w-32"
                            />
                            <button
                              type="button"
                              onClick={() => removeChangeItem(index)}
                              className="w-7 h-7 rounded-lg text-rose-500 hover:bg-rose-50 flex items-center justify-center transition-colors"
                              title="حذف هذا البند"
                            >
                              <i className="ri-delete-bin-line text-sm" />
                            </button>
                          </div>
                        </div>

                        <div>
                          <input
                            type="text"
                            placeholder="عنوان التغيير (مثال: إضافة قارئ الباركود اللاسلكي)..."
                            value={item.title}
                            onChange={(e) => updateChangeItem(index, 'title', e.target.value)}
                            className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm font-medium bg-white focus:outline-none focus:border-blue-500"
                          />
                        </div>

                        <div>
                          <textarea
                            rows={2}
                            placeholder="التفاصيل الدقيقة للتغيير وكيف يستفيد منه المستخدم (اختياري)..."
                            value={item.description ?? ''}
                            onChange={(e) => updateChangeItem(index, 'description', e.target.value)}
                            className="w-full px-3 py-2 rounded-lg border border-gray-200 text-xs bg-white focus:outline-none focus:border-blue-500"
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Card 3: Compatibility, System Requirements, Upgrade Instructions */}
            <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm space-y-4">
              <div className="flex items-center gap-2 border-b border-gray-100 pb-3 text-base font-bold text-gray-900">
                <i className="ri-cpu-line text-indigo-600 text-lg" />
                متطلبات التشغيل والتوافق والترقية
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                    الحد الأدنى لمتطلبات التشغيل (System Requirements)
                  </label>
                  <textarea
                    rows={3}
                    placeholder="مثال: Windows 10/11 64-bit، معالج 2.0 GHz، ذاكرة 4GB RAM، مساحة تخزين 1GB"
                    value={systemRequirements}
                    onChange={(e) => setSystemRequirements(e.target.value)}
                    className="w-full px-3 py-2 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                    ملاحظات التوافق (Compatibility Notes)
                  </label>
                  <textarea
                    rows={3}
                    placeholder="مثال: متوافق مع قواعد بيانات الإصدار 2.3 فما فوق دون الحاجة للترحيل اليدوي"
                    value={compatibilityNotes}
                    onChange={(e) => setCompatibilityNotes(e.target.value)}
                    className="w-full px-3 py-2 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-blue-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                  تعليمات وإرشادات الترقية (Upgrade Instructions)
                </label>
                <textarea
                  rows={2}
                  placeholder="مثال: يوصى بأخذ نسخة احتياطية من قاعدة البيانات ثم تشغيل برنامج التثبيت ليقوم بالتحديث تلقائياً دون فقد أي بيانات"
                  value={upgradeInstructions}
                  onChange={(e) => setUpgradeInstructions(e.target.value)}
                  className="w-full px-3 py-2 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-blue-500"
                />
              </div>
            </div>

            {/* Card 4: Desktop Installer Upload (Artifacts) */}
            {isDesktopProduct && (
              <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm space-y-4">
                <div className="flex items-center justify-between border-b border-gray-100 pb-3">
                  <div className="flex items-center gap-2 text-base font-bold text-gray-900">
                    <i className="ri-download-cloud-2-line text-blue-600 text-lg" />
                    إدارة ملفات التنزيل والتثبيت الحقيقية (Desktop Artifacts)
                  </div>
                  <span className="text-xs text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded-full font-semibold border border-emerald-200">
                    التحقق الموثوق عبر بصمة SHA-256
                  </span>
                </div>

                {!isEdit ? (
                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-800">
                    <i className="ri-information-line text-sm inline-block mr-1" />
                    يرجى حفظ الإصدار أولاً للتمكن من رفع وربط ملفات التثبيت وحساب بصمات التحقق لها.
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Existing Artifacts */}
                    {artifacts.length > 0 && (
                      <div className="space-y-2">
                        {artifacts.map((art) => (
                          <div
                            key={art.id}
                            className="p-4 rounded-xl border border-gray-200 bg-gray-50 flex flex-col md:flex-row md:items-center justify-between gap-3"
                          >
                            <div className="space-y-1">
                              <div className="flex items-center gap-2">
                                <i className="ri-file-zip-line text-xl text-blue-600" />
                                <span className="font-bold text-gray-900 text-sm">{art.original_name}</span>
                                <span className="text-xs text-gray-500">
                                  ({(art.file_size / (1024 * 1024)).toFixed(2)} MB)
                                </span>
                                <span className="text-xs font-semibold px-2 py-0.5 rounded bg-blue-100 text-blue-800 uppercase">
                                  {art.os_type} {art.architecture}
                                </span>
                              </div>

                              {art.file_hash && (
                                <div className="flex items-center gap-2 text-xs font-mono text-gray-600 bg-white px-2.5 py-1 rounded-md border border-gray-200 w-fit">
                                  <span className="text-gray-400 font-sans text-[11px]">SHA-256:</span>
                                  <span className="select-all">{art.file_hash}</span>
                                </div>
                              )}

                              <div className="text-[11px] text-gray-400">
                                إجمالي مرات التنزيل: <span className="font-bold text-gray-700">{art.download_count}</span>
                              </div>
                            </div>

                            <div className="flex items-center gap-2 shrink-0">
                              {art.download_url && (
                                <a
                                  href={art.download_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="px-3 py-1.5 rounded-lg bg-blue-50 text-blue-700 hover:bg-blue-100 text-xs font-semibold flex items-center gap-1"
                                >
                                  <i className="ri-download-2-line" />
                                  اختبار الرابط
                                </a>
                              )}

                              <button
                                type="button"
                                onClick={() => void handleDeleteArtifact(art.id)}
                                className="px-3 py-1.5 rounded-lg bg-rose-50 text-rose-700 hover:bg-rose-100 text-xs font-semibold flex items-center gap-1"
                              >
                                <i className="ri-delete-bin-line" />
                                حذف
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Upload new artifact box */}
                    <div className="p-4 rounded-xl border border-dashed border-gray-300 bg-gray-50/50 space-y-3">
                      <div className="text-xs font-bold text-gray-800">رفع ملف تثبيت جديد لهذا الإصدار:</div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <div>
                          <label className="block text-[11px] font-semibold text-gray-600 mb-1">نظام التشغيل</label>
                          <select
                            value={artifactOs}
                            onChange={(e) => setArtifactOs(e.target.value)}
                            className="w-full px-2.5 py-1.5 rounded-lg border border-gray-200 text-xs bg-white"
                          >
                            <option value="windows">Windows</option>
                            <option value="macos">macOS</option>
                            <option value="linux">Linux</option>
                          </select>
                        </div>

                        <div>
                          <label className="block text-[11px] font-semibold text-gray-600 mb-1">المعمارية</label>
                          <select
                            value={artifactArch}
                            onChange={(e) => setArtifactArch(e.target.value)}
                            className="w-full px-2.5 py-1.5 rounded-lg border border-gray-200 text-xs bg-white"
                          >
                            <option value="x64">64-bit (x64 / AMD64)</option>
                            <option value="x86">32-bit (x86)</option>
                            <option value="arm64">Apple Silicon / ARM64</option>
                          </select>
                        </div>

                        <div>
                          <label className="block text-[11px] font-semibold text-gray-600 mb-1">الحد الأدنى للنظام</label>
                          <input
                            type="text"
                            value={artifactMinOs}
                            onChange={(e) => setArtifactMinOs(e.target.value)}
                            placeholder="Windows 10+"
                            className="w-full px-2.5 py-1.5 rounded-lg border border-gray-200 text-xs bg-white"
                          />
                        </div>
                      </div>

                      <div className="pt-2">
                        <label className="inline-flex items-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs font-bold cursor-pointer transition-colors shadow-sm">
                          {uploadingArtifact ? (
                            <i className="ri-loader-4-line animate-spin" />
                          ) : (
                            <i className="ri-upload-2-line" />
                          )}
                          <span>
                            {uploadingArtifact ? 'جاري رفع الملف وحساب التجزئة...' : 'اختيار ملف التثبيت (.exe, .zip, .msi)'}
                          </span>
                          <input
                            type="file"
                            onChange={(e) => void handleFileUpload(e)}
                            disabled={uploadingArtifact}
                            className="hidden"
                          />
                        </label>
                        <span className="text-[11px] text-gray-400 mr-3">
                          يتم فحص سلامة الملف وحساب بصمة SHA-256 فور اكتمال الرفع تلقائياً.
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
